/**
 * Delegated user-interaction tracking for the MDIE app.
 *
 * @remarks
 *
 * The app ships as a Web Component (`<market-data-center>`) rendered inside a
 * Shadow DOM. A click that bubbles out of the shadow root is *retargeted* by the
 * browser: to the host document, `event.target` becomes the host
 * `<market-data-center>` element rather than the real inner element. GTM's
 * built-in auto-event listeners attach to the host document and are therefore
 * blind inside the shadow DOM.
 *
 * The fix is `event.composedPath()`, which returns the full, non-retargeted node
 * path, piercing the shadow boundary (`composedPath()[0]` is the true innermost
 * target). We build our own delegated listeners on the shadow root (or the host
 * document in the standalone build) and read `composedPath()` — the same pattern
 * `DefinitionDrawer`'s outside-click handler already uses.
 *
 * This module is the generalized mechanism only: a delegated click listener
 * (anchor vs button split) and a generalized tooltip hover/focus listener. No
 * scroll-depth or time-on-page tracking (out of scope this phase), and no
 * page-specific `data-track` annotations.
 *
 * PII guard: listeners never read an input's `value` or the typed search query.
 */

import { pushEvent } from './gtm';

/**
 * Elements the click listener treats as trackable. Buttons and anchors both
 * fire `click`; `[data-track]` lets a call site opt a wrapper in explicitly.
 */
const CLICK_INTERACTIVE =
  'button, a, [role="button"], input[type="submit"], [data-track]';

/**
 * Elements the tooltip listener treats as a reveal trigger. Kept to explicit
 * annotations so the generalized listener does not fire on every hovered
 * element; `data-track-tooltip` / `data-track="definition_tooltip"` are added
 * per tooltip later for enrichment, with no code change needed here.
 */
const TOOLTIP_TRIGGER = '[data-track-tooltip], [data-track="definition_tooltip"]';

const LABEL_MAX_LENGTH = 100;

/**
 * Walks the composed path outward from the true target and returns the first
 * element matching `selector`, stopping at the listener root so we never climb
 * past our own boundary. Returns `null` when nothing matches (noise control).
 */
function resolveTrackable(
  event: Event,
  root: Document | ShadowRoot,
  selector: string,
): Element | null {
  for (const node of event.composedPath()) {
    if (node instanceof Element && node.matches(selector)) {
      return node;
    }
    if (node === root) {
      break;
    }
  }

  return null;
}

/**
 * Trims surrounding whitespace and caps length to protect GA4 cardinality.
 * Returns `undefined` for empty strings so an absent label stays absent.
 */
function truncate(value: string | null | undefined): string | undefined {
  const text = value?.trim();

  if (!text) {
    return undefined;
  }

  return text.length > LABEL_MAX_LENGTH
    ? text.slice(0, LABEL_MAX_LENGTH)
    : text;
}

/**
 * A stable, low-cardinality descriptor for unannotated elements, e.g. `button`,
 * `a`, `input[type="submit"]`, `[role="button"]`.
 */
function describeElement(el: Element): string {
  const tag = el.tagName.toLowerCase();

  if (tag === 'input') {
    const type = el.getAttribute('type');
    return type ? `input[type="${type}"]` : 'input';
  }

  if (tag !== 'button' && tag !== 'a') {
    const role = el.getAttribute('role');
    if (role) {
      return `[role="${role}"]`;
    }
  }

  return tag;
}

/**
 * Derives a human-readable label without ever reading an input's `value`
 * (PII guard): `data-track-label`, then `aria-label`, then trimmed text.
 */
function deriveLabel(el: Element): string | undefined {
  return (
    truncate(el.getAttribute('data-track-label')) ??
    truncate(el.getAttribute('aria-label')) ??
    truncate(el.textContent)
  );
}

/**
 * Classifies an anchor as `external` when it opens a new tab or points at a
 * different origin; otherwise `internal` (TanStack `<Link>` renders a relative
 * `href`, which resolves to the same origin).
 */
function classifyLink(anchor: HTMLAnchorElement): 'internal' | 'external' {
  if (anchor.target === '_blank') {
    return 'external';
  }

  const href = anchor.getAttribute('href');

  if (!href) {
    return 'internal';
  }

  try {
    const resolved = new URL(href, window.location.href);
    return resolved.origin === window.location.origin ? 'internal' : 'external';
  } catch {
    // Relative or malformed hrefs resolve same-origin / are in-app.
    return 'internal';
  }
}

/**
 * Attaches delegated interaction listeners to `target` (the shadow root when
 * embedded, else the host document) and returns a teardown fn that removes every
 * listener. SSR/boot-safe: a no-op returning an empty teardown when there is no
 * DOM. Fully removing listeners is required so React StrictMode's dev
 * double-mount does not leak listeners or double-fire events.
 *
 * @param target - The shadow root (embed) or document (standalone build).
 * @param getPagePath - Reads the live SPA route path for `page_path`.
 * @returns A cleanup function that detaches all listeners.
 */
export function initInteractionTracking(
  target: Document | ShadowRoot,
  getPagePath: () => string,
): () => void {
  // SSR/boot-safe: nothing to attach without a DOM.
  if (typeof window === 'undefined') {
    return () => {};
  }

  // De-dupe tooltip reveals: one event per trigger per visibility cycle. Cleared
  // on hide so a later re-hover re-counts.
  const tooltipShown = new WeakSet<Element>();

  function onClick(event: Event) {
    const el = resolveTrackable(event, target, CLICK_INTERACTIVE);

    if (!el) {
      return;
    }

    const base = {
      element: el.getAttribute('data-track') ?? describeElement(el),
      label: deriveLabel(el),
      context: el.getAttribute('data-track-context') ?? undefined,
      page_path: getPagePath(),
    };

    const anchor =
      el instanceof HTMLAnchorElement ? el : el.closest('a');

    if (anchor) {
      pushEvent({
        event: 'mdc_link_click',
        ...base,
        href: anchor.getAttribute('href') ?? undefined,
        link_type: classifyLink(anchor),
      });
      return;
    }

    pushEvent({ event: 'mdc_click', ...base });
  }

  function onTooltipReveal(event: Event) {
    const el = resolveTrackable(event, target, TOOLTIP_TRIGGER);

    if (!el || tooltipShown.has(el)) {
      return;
    }

    tooltipShown.add(el);

    pushEvent({
      event: 'mdc_tooltip_view',
      element: el.getAttribute('data-track') ?? 'definition_tooltip',
      label: deriveLabel(el),
      context: el.getAttribute('data-track-context') ?? 'glossary_tooltip',
      term_slug: el.getAttribute('data-track-term-slug') ?? undefined,
      page_path: getPagePath(),
    });
  }

  function onTooltipHide(event: Event) {
    const el = resolveTrackable(event, target, TOOLTIP_TRIGGER);

    if (el) {
      // Clear so a genuine later re-hover/re-focus counts again.
      tooltipShown.delete(el);
    }
  }

  // Capture phase so we still observe the event even if an inner handler calls
  // stopPropagation().
  target.addEventListener('click', onClick, true);
  target.addEventListener('mouseover', onTooltipReveal, true);
  target.addEventListener('focusin', onTooltipReveal, true);
  target.addEventListener('mouseout', onTooltipHide, true);
  target.addEventListener('focusout', onTooltipHide, true);

  return () => {
    target.removeEventListener('click', onClick, true);
    target.removeEventListener('mouseover', onTooltipReveal, true);
    target.removeEventListener('focusin', onTooltipReveal, true);
    target.removeEventListener('mouseout', onTooltipHide, true);
    target.removeEventListener('focusout', onTooltipHide, true);
  };
}
